/**
 * Admin → Live Ops (voice monitoring).
 *
 * Backed by GET /v1/admin/voice-ops. Same gate as the rest of /v1/admin/*:
 * the RequireReseller middleware returns 403 to anyone whose JWT claims
 * tenant_type isn't platform/reseller, so the sidebar link is hidden for
 * non-resellers and the route guard blocks direct URL access.
 *
 * The endpoint is 100% read-only — one row per voice campaign currently
 * running (all tenants for platform admin, the caller's customers for a
 * reseller) with live-ish CPS/concurrency, today's answer/AMD/transfer/603
 * mix, lead pileup, and balance. Poll on an interval for auto-refresh.
 *
 * The payload ALSO carries campaigns paused within the last 7 days (bounded
 * server-side), which back the Operations center's "Paused" panel — and, for
 * the ~35s a restart toggle parks a campaign in 'paused', keep its row alive
 * in the live table. `status` + `restarting` tell the two apart; the consumer
 * decides which surface a row belongs to.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface VoiceOpsCampaign {
  /** Detector in force ("heuristic" | "dynamd"). Platform-set from this
   *  table — the tenant cannot change it on the campaign itself. */
  amd_engine?: string;
  tenant_id: number;
  tenant_name: string;
  campaign_id: number;
  campaign_name: string;
  status: string;
  trunk_id: number;
  /** sip_trunks.gateway_name; empty when the trunk row is missing. */
  trunk: string;
  /** sip_trunks.outbound_prepend_code; empty when unset. */
  prefix: string;
  /** voice_campaigns.pacing_calls_per_minute — configured target dial rate. */
  pacing_cpm: number;
  /** voice_campaigns.max_concurrent_calls; 0 = backend default / unlimited. */
  max_concurrent: number;
  /**
   * True when max_concurrent is a positive value set so low that it, not
   * pacing, is the binding cap on throughput (see the backend heuristic).
   */
  concurrency_capped: boolean;
  /** Human-readable explanation of the concurrency cap; '' when not capped. */
  concurrency_warning: string;
  /** Live dial rate: dials in the last 60s / 60, 1 decimal. */
  cps: number;
  /**
   * Actual dials/sec over the trailing 60s, 2 decimals — the "actual" half of
   * the CPS column. Finer than `cps` so a concurrency-capped campaign dialing
   * ~0.03 CPS stays legible instead of rounding to 0.0.
   */
  cps_current: number;
  /** Configured per-second dial target: pacing_calls_per_minute / 60, 2 decimals. 0 when unset. */
  cps_target: number;
  /**
   * APPROXIMATE active calls: call_records with status='initiated' in the
   * trailing 90s. Over-reads the true FreeSWITCH concurrency (~1.3-1.6x)
   * because of DB-writeback lag — labelled "~Active" in the UI. Exact
   * concurrency would require querying FreeSWITCH.
   */
  active_approx: number;
  /** voice_campaign_leads.status='incall' — the pileup indicator. */
  incall_leads: number;
  /** voice_campaign_leads.status IN ('new','retry') — still dialable. */
  remaining: number;
  /** Today's cumulative (initiated_at >= current_date). */
  answered: number;
  machine: number;
  human: number;
  transfers: number;
  /** Today's presses on the flow's opt-out key(s); 0 when the flow has none. */
  optouts: number;
  total: number;
  c603: number;
  balance: number;
  credit_limit: number;
  /** balance + credit_limit. */
  available: number;
  /**
   * Dials in the trailing 5 minutes — the denominator of the two rates below.
   * Also the confidence signal: a 100% failure rate over 2 dials means nothing,
   * so the UI suppresses the percentages under RECENT_FAIL_MIN_DIALS.
   */
  recent_dials: number;
  /** Trailing-5min dials refused with SIP 503 / NORMAL_TEMPORARY_FAILURE. */
  recent_503: number;
  /** Trailing-5min dials that never connected and carry an explicit rejection. */
  recent_failed: number;
  /**
   * recent_503 / recent_dials, 0..1. THE pacing feedback signal: it means the
   * carrier is refusing the offered rate. Measured 2026-09-03 — pacing 900/min
   * gave ~0.76, dropping to 360/min gave ~0.15.
   */
  recent_503_pct: number;
  /** recent_failed / recent_dials, 0..1 — the broader never-connected rate. */
  recent_fail_pct: number;
  /**
   * True while an admin "restart runner" toggle is holding this campaign in
   * 'paused' waiting out a poller scan cycle (~35s). The row is paused on
   * purpose — render "restarting…", not "someone paused this".
   */
  restarting: boolean;
  /**
   * RFC3339 timestamp of the campaign's last status change — its "paused
   * since" for the paused panel. Present only on rows that are NOT running
   * (the API omits the field for a running campaign).
   */
  paused_at?: string;
}

/**
 * Result of a campaign action endpoint. `restarting` + `resume_in_seconds` are
 * only meaningful for restart-runner.
 */
export interface CampaignActionResult {
  campaign_id: number;
  tenant_id: number;
  action: 'pause' | 'resume' | 'restart_runner' | 'update_pacing';
  status: string;
  restarting: boolean;
  resume_in_seconds: number;
  pacing_calls_per_minute: number;
  max_concurrent_calls: number;
}

/** Poll cadence for the live auto-refresh (ms). */
export const VOICE_OPS_REFETCH_MS = 10_000;

const VOICE_OPS_KEY = ['admin', 'voice-ops'] as const;

/**
 * Fetch for /admin/voice-ops. When `live` is true the query re-polls every
 * VOICE_OPS_REFETCH_MS; pausing (live=false) stops the interval so the
 * operator can freeze the table to read it.
 */
export function useAdminVoiceOps(opts?: { live?: boolean; enabled?: boolean }) {
  const live = opts?.live ?? true;
  return useQuery({
    queryKey: VOICE_OPS_KEY,
    queryFn: () => api.get<VoiceOpsCampaign[]>('/v1/admin/voice-ops'),
    refetchInterval: live ? VOICE_OPS_REFETCH_MS : false,
    refetchOnWindowFocus: live,
    staleTime: 5_000,
    enabled: opts?.enabled ?? true,
  });
}

/** The three-way outcome of a transfer attempt (see VoiceOpsTransfer.state). */
export type VoiceOpsTransferState = 'connected' | 'failed' | 'in_progress';

/**
 * One transfer ATTEMPT made today — a single call, not a contact. A contact
 * redialed and transferred twice appears twice; this is an operational log,
 * not the per-contact final-outcome list a campaign report shows.
 */
export interface VoiceOpsTransfer {
  tenant_id: number;
  tenant_name: string;
  campaign_id: number;
  campaign_name: string;
  /** RFC3339 (UTC) time the CALL was dialed. call_records has no timestamp for the bridge itself. */
  initiated_at: string;
  /** The contact who was transferred. */
  to_e164: string;
  /** Where the transfer was aimed. */
  bridged_to: string;
  /**
   * Where the attempt stands. This — NOT `status` — is the outcome split: an
   * A-leg that hung up normally reports status='completed' whether or not its
   * transfer reached anyone.
   *
   *   - `connected`: the call has ended and the bridge carried audio.
   *   - `failed`: the call has ended and the bridge never connected.
   *   - `in_progress`: the call is still up. bridge_seconds and cause are
   *     written on hangup, so nothing is known yet — render it as neutral,
   *     never as a failure.
   */
  state: VoiceOpsTransferState;
  /** `state === 'connected'` — kept for callers that only need the boolean. */
  connected: boolean;
  /** Seconds the two legs stayed bridged; 0 whenever `connected` is false. */
  bridge_seconds: number;
  /**
   * FreeSWITCH's reason a bridge failed (USER_BUSY, NO_ANSWER,
   * INCOMPATIBLE_DESTINATION, …). Empty when FreeSWITCH reported none — render
   * that as "unknown", never as success — and always empty while in progress.
   */
  cause: string;
  /** The call's own status (completed, failed, …). Describes the A-leg only. */
  status: string;
}

/**
 * One hour bucket of today's transfer attempts — a single bar of the
 * drill-down's hourly chart.
 *
 * `hour` is the hour of the day 0-23 in UTC, which is the zone the endpoint's
 * "today" boundary is evaluated in. The chart labels its axis UTC for that
 * reason: re-localizing the buckets to the browser would put the first one on
 * yesterday and stop the series agreeing with the header's counters.
 *
 * The series is DENSE — every hour from 00 through the current one is present,
 * zero-filled where nothing was attempted — so the axis never silently
 * rescales around a missing hour.
 */
export interface VoiceOpsTransfersHour {
  /** 0-23, UTC. */
  hour: number;
  /** RFC3339 start of the bucket (today's date at `hour`), for the tooltip. */
  hour_start: string;
  /** Transfer ATTEMPTS in this hour. total = connected + failed + in_progress. */
  total: number;
  connected: number;
  failed: number;
  /** Still on the phone when the page was loaded — outcome not known yet. */
  in_progress: number;
}

/**
 * A page of today's transfers. `total` / `connected` / `failed` /
 * `in_progress` count EVERY matching transfer, not just the returned page, so
 * growing the page size never moves the header's numbers.
 * total = connected + failed + in_progress.
 */
export interface VoiceOpsTransfersPage {
  items: VoiceOpsTransfer[];
  total: number;
  connected: number;
  failed: number;
  in_progress: number;
  /**
   * Today bucketed by hour. Its own server-side aggregate over the whole
   * matching set, NOT a rollup of `items` — the chart stays accurate when the
   * list is capped at TRANSFERS_TODAY_MAX.
   */
  hourly: VoiceOpsTransfersHour[];
}

/** First page size for the transfers drill-down, and the step "show more" adds. */
export const TRANSFERS_TODAY_PAGE = 100;
/** Server-side ceiling (api/internal/services/admin_voice_ops_transfers.go). */
export const TRANSFERS_TODAY_MAX = 500;

/**
 * Fetch for /admin/voice-ops/transfers-today — the drill-down behind the
 * Operations center's "Transfers today" tile. Same gate and same campaign
 * scoping as useAdminVoiceOps (the backend shares the selection CTE), so this
 * list can never show a campaign the table above it hides.
 *
 * Enabled only while the modal is open: this is a click-through, not part of
 * the 10s live poll, and a closed modal must not keep querying. `limit` is
 * part of the query key so "show more" refetches instead of reusing the
 * smaller page.
 */
export function useAdminVoiceOpsTransfersToday(opts: { enabled: boolean; limit?: number }) {
  const limit = opts.limit ?? TRANSFERS_TODAY_PAGE;
  return useQuery({
    queryKey: [...VOICE_OPS_KEY, 'transfers-today', limit] as const,
    queryFn: () =>
      api.get<VoiceOpsTransfersPage>(`/v1/admin/voice-ops/transfers-today?limit=${limit}`),
    enabled: opts.enabled,
    staleTime: 5_000,
  });
}

/**
 * Minimum trailing-5min dials before the failure percentages are shown at all.
 * Below this the ratio is noise (1 failed call out of 2 is not "50% failing"),
 * and an operator must not retune pacing off it.
 */
export const RECENT_FAIL_MIN_DIALS = 10;

/**
 * Campaign actions on /admin/ops. All four are platform-admin only (the API
 * enforces tenant_type=platform on top of the admin role guard) and all four
 * invalidate the Live Ops query so the table reflects the new state on the
 * next tick.
 *
 * Pause / activate go through the campaign service's transition graph, so a
 * rejected transition comes back as a 409 rather than silently doing nothing.
 *
 * Restart-runner is the poller-registry toggle: it returns 202 immediately
 * with the campaign PAUSED, and the API resumes it ~35s later (a full poller
 * scan cycle must observe the not-running state, or the stale runner entry
 * never clears). The row carries `restarting: true` for that window.
 *
 * Pacing applies LIVE — the running runner re-reads the columns within
 * seconds. No restart, no dropped calls.
 */
export function useCampaignOpsActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: VOICE_OPS_KEY });
  };

  const pause = useMutation({
    mutationFn: (campaignId: number) =>
      api.post<CampaignActionResult>(`/v1/admin/voice-ops/campaigns/${campaignId}/pause`),
    onSuccess: invalidate,
  });

  const resume = useMutation({
    mutationFn: (campaignId: number) =>
      api.post<CampaignActionResult>(`/v1/admin/voice-ops/campaigns/${campaignId}/resume`),
    onSuccess: invalidate,
  });

  const restart = useMutation({
    mutationFn: (campaignId: number) =>
      api.post<CampaignActionResult>(`/v1/admin/voice-ops/campaigns/${campaignId}/restart-runner`),
    onSuccess: invalidate,
  });

  const updatePacing = useMutation({
    mutationFn: (vars: { campaignId: number; pacingCpm?: number; maxConcurrent?: number }) =>
      api.patch<CampaignActionResult>(`/v1/admin/voice-ops/campaigns/${vars.campaignId}/pacing`, {
        ...(vars.pacingCpm != null ? { pacing_calls_per_minute: vars.pacingCpm } : {}),
        ...(vars.maxConcurrent != null ? { max_concurrent_calls: vars.maxConcurrent } : {}),
      }),
    onSuccess: invalidate,
  });

  const updateAmdEngine = useMutation({
    mutationFn: (vars: { campaignId: number; engine: string }) =>
      api.patch<CampaignActionResult>(
        `/v1/admin/voice-ops/campaigns/${vars.campaignId}/amd-engine`,
        { amd_engine: vars.engine },
      ),
    onSuccess: invalidate,
  });

  return { pause, resume, restart, updatePacing, updateAmdEngine };
}
